import React, { useState, useEffect, useCallback } from "react";
import { Modal, notification, ConfigProvider, Checkbox } from "antd";

// Import sub-components
import CategoryNameInput from "../../component/restaurants/categories/CategoryNameInput";
import CategoryVariationRows from "../../component/restaurants/categories/CategoryVariationRows";

// Import custom hooks
import useCategoryData from "../../hooks/category/useCategoryData";
import useFormValidation from "../../hooks/category/useFormValidation";

// Import services and utilities
import ApiService from "../../utils/ApiService";
import useUserStore from "../../stores/store";
import { adjustDefaultVariants } from "../../services/mixed-service";
import ImageUploadWithCropAndWebp from "../common/ImageUploadWithCropAndWebp";
import useScrollLock from "../../hooks/useScrollLock";

function AddOrEditCategoryModalV2({
  openModal,
  onFinish,
  onClose,
  mode,
  categoryData,
  fetchCategoryList
}) {
  const { user, restaurantId, setNetworkCall, showNotification } = useUserStore();
  const [api, contextHolder] = notification.useNotification();
  const [isSetMenu, setIsSetMenu] = useState(false);
  const [isDataAdded, setIsDataAdded] = useState(false);
  // Lock scroll when modal is open
  useScrollLock(openModal);
  const {
    categoryDetails,
    categoryName,
    setCategoryName,
    options,
    fileList,
    setFileList,
    rows,
    setRows,
    originalRows,
    actionMode,
    isInitialEditMode,
    setIsInitialEditMode,
    setActionMode,
    handleCategorySearch,
    handleCategorySelect,
    clearSuggestions,
    resetRows,
    hasMultipleOptions,
    setHasMultipleOptions,
    fetchCategorySuggestions
  } = useCategoryData(mode, categoryData, user, restaurantId, setIsSetMenu);

  const { validateForm } = useFormValidation();

  const clearState = useCallback(() => {
    clearSuggestions();
    setCategoryName("");
    setFileList([]);
    resetRows(); // Use resetRows from useCategoryData
    setIsSetMenu(false);
    setActionMode(mode);
  }, [
    clearSuggestions,
    setCategoryName,
    setFileList,
    resetRows,
    setActionMode,
    mode,
  ]);

  // Reset isSetMenu when category name changes
  useEffect(() => {
    if (!isInitialEditMode && actionMode !== "edit") {
      setIsSetMenu(false);
    }
  }, [categoryName, isInitialEditMode, actionMode]);

  useEffect(() => {
    if (mode === "add") {
      setIsInitialEditMode(false);
      clearState();
    } else {
      setIsInitialEditMode(true);
    }
  }, [mode]);

  useEffect(() => {
    function assignMultipleOptionsValue() {
      const tempRows = rows.filter(row => row.name !== 'System Default');
      if (tempRows.length > 0) {
        const hasMultiple = rows.some((row) => row.name && row.weight);
        setHasMultipleOptions(hasMultiple);
      } else {
        setHasMultipleOptions(false);
      }
    }

    assignMultipleOptionsValue();
    
  }, [rows]);

  const handleOk = async () => {
    try {
      // Validate form inputs
      const adjustedVariants = adjustDefaultVariants(rows, isSetMenu);

      const validationResult = validateForm(
        categoryName,
        adjustedVariants,
        isSetMenu,
        fileList
      );

      if (!validationResult.isValid) {
        notification.error({
          message: "Validation Error",
          description: validationResult.errorMessage,
        });
        return;
      }

      // Filter and format category options
      const updatedRows = adjustedVariants.map((row) => {
        if (actionMode === "add") {
          // For add mode, set optionId as empty string
          return {
            ...row,
            isOptionRemoved: "No",
            optionId: "",
          };
        } else {
          // For edit mode, check if the row exists in originalRows
          const originalRow = originalRows.find((r) => r.id === row.id);
          return {
            ...row,
            isOptionRemoved: "No",
            optionId: originalRow ? row.id : "", // Keep existing optionId for original rows, empty for new rows
          };
        }
      });

      // For edit mode only: Check for deleted rows from originalRows
      if (actionMode === "edit") {
        originalRows.forEach((originalRow) => {
          const rowStillExists = rows.some((row) => row.id === originalRow.id);
          if (!rowStillExists) {
            // Add deleted original rows with isOptionRemoved = "Yes"
            updatedRows.push({
              ...originalRow,
              isOptionRemoved: "Yes",
              optionId: originalRow.id,
            });
          }
        });
      }

      const categoryId = (actionMode === "edit" && categoryDetails.categoryId)
          ? categoryDetails.categoryId.toString()
          : "";

      let currentRows = updatedRows.filter(item => item.isOptionRemoved.toLowerCase() !== 'yes')

      if (currentRows?.length > 1) {
        currentRows = currentRows.filter(item => item.name !== "System Default")
      }
      
      // validation message if category is already exists when create mode
      if (actionMode === "add") {
        if (options.some(op => op.value === categoryName)) {
          showNotification("Category name already exists! Please edit category if needed.");
          return;
        }
      }

      let payload = {};
      if (hasMultipleOptions) {
        payload = {
          categoryId,
          categoryName: categoryName.trim(),
          restaurantId: restaurantId.toString(),
          isSetMenu: isSetMenu ? "Yes" : "No",
          hasMultipleOptions: !isSetMenu && hasMultipleOptions ? "Yes" : "No",
          // hasMultipleOptions: hasMultipleOptions ? "Yes" : "No",
          categoryOptions: JSON.stringify({
            options: isSetMenu
              ? []
              : currentRows.map((row) => ({
                  optionId: row.optionId,
                  name: row.name.trim(),
                  weight: parseFloat(row.weight),
                  isOptionRemoved: row.isOptionRemoved,
                })),
          }),
        };
      } else {
        payload = {
          categoryId,
          categoryName: categoryName.trim(),
          restaurantId: restaurantId.toString(),
          isSetMenu: isSetMenu ? "Yes" : "No",
          hasMultipleOptions: !isSetMenu && hasMultipleOptions ? "Yes" : "No",
          //hasMultipleOptions: hasMultipleOptions ? "Yes" : "No",
        };
      }

      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined) {
          formData.append(key, value);
        }
      });

      const uploadedFile = fileList[0]?.originFileObj;
      if (uploadedFile) {
        formData.append("categoryImage", uploadedFile);
      }

      setNetworkCall(true);
      const token = user?.token?.token;
      const res = await ApiService.UPSERT_CATEGORY(formData, token);

      if (res.code !== 200) {
        showNotification(res.data.message)
        return;
      }

      if (res.code === 200) {
        handleCancel();
        clearState();
        fetchCategoryList();
        fetchCategorySuggestions();
        setIsDataAdded(true);
        showNotification("Data updated successfully.", false)
        return;
      }

      notification.success({
        message: "Success",
        description: `Successfully ${
          actionMode === "add" ? "added" : "updated"
        } category.`,
      });

      onFinish();
      handleCancel();
      clearState();
    } catch (error) {
      console.error("Category Submission Error:", error);
      notification.error({
        message: "Submission Failed",
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setNetworkCall(false);
    }
  };

  const handleCancel = () => {
    clearSuggestions();
    setIsSetMenu(false);
    onClose();
  };

  useEffect(() => {
    if (categoryDetails && categoryDetails.isSetMenu) {
      setIsSetMenu(categoryDetails.isSetMenu === "No" ? false : true);
    }
  }, [categoryDetails]);

  function onHandleHasMultipleOptions(isChecked) {
    setHasMultipleOptions(isChecked);
  }

  const deleteImage = async () => {
    try {
      if (!categoryDetails?.categoryId) {
        setFileList([]);
        return
      }

      setNetworkCall(true);
      const token = user?.token?.token;
      const params = {
        context: "CATEGORY_EDIT",
        fieldName: "image",
        modelId: categoryDetails?.categoryId,
        restaurantId
      }
      const res = await ApiService.ADMIN_FILE_DELETE(params, token);

      if (res.code !== 200) {
        showNotification(res.data.message)
        return;
      }

      if (res.code === 200) {
        setFileList([])
        fetchCategoryList()
        showNotification("Image deleted successfully.", false)
        return;
      }
      
    } catch (error) {
      console.error("Category image delete ==== ", error)
      showNotification(error.message || "An unexpected error occurred.")
    } finally {
      setNetworkCall(false);
    }
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: "#FFD700",
            defaultHoverColor: "#000000",
            fontFamily: "ubold",
          },
        },
        token: {
          colorPrimary: "#FCBD10",
        },
      }}
    >
      {contextHolder}
      <Modal
        maskClosable={false}
        open={openModal}
        onOk={handleOk}
        onCancel={handleCancel}
        //title={`${actionMode === "add" ? "Add New" : "Edit"} Category`} // INHS-1597
        title={`${mode === "add" ? "Add New" : "Edit"} Category`}
        okText={mode === "add" ? "Create" : "Update"}
        okButtonProps={{ style: { backgroundColor: "#FFD700", color: "#000" } }}
      >
        <CategoryNameInput
          mode={actionMode}
          isInitialEditMode={isInitialEditMode}
          categoryName={categoryName}
          options={options}
          onSearch={handleCategorySearch}
          onSelect={handleCategorySelect}
          onClear={clearSuggestions}
        />

        <Checkbox
          checked={isSetMenu}
          onChange={(e) => setIsSetMenu(e.target.checked)}
          style={{ marginTop: "1rem" }}
          className="mb-6"
          disabled={actionMode === "edit"}
        >
          Is it a Set Menu?
        </Checkbox>

        <div className="mb-[3px] font-ubold">Category Image</div>
        <ImageUploadWithCropAndWebp fileList={fileList} setFileList={setFileList} deleteImage={deleteImage} />

        <CategoryVariationRows
          rows={rows}
          setRows={setRows}
          originalRows={originalRows}
          disabled={isSetMenu}
          hasMultipleOptions={hasMultipleOptions}
          handleHasMultipleOptions={onHandleHasMultipleOptions}
        />
      </Modal>
    </ConfigProvider>
  );
}

export default AddOrEditCategoryModalV2;
